import { createRouter, createWebHistory } from 'vue-router'

const HTML_EXTENSION = '.html';

const routes = [
  {
    path: '/:lang(en|de|ru|ua|hy)?',
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('../views/Home.vue')
      },
      {
        path: 'quizzes',
        name: 'SimpleChoiceQuiz',
        component: () => import('../components/quizEngines/SimpleChoice/SimpleChoiceRunner.vue'),
        props: route => ({
          taskId: route.query.task || 'possessive_pronouns',
          level: route.query.level || 'a1',
          lesson: route.query.lesson || 3
        })
      },
      // TEMPORARY FALLBACK / COMPATIBILITY REDIRECT:
      // Legacy quiz pages (e.g. /quizzes/Modal_verbs_past.html) are standalone static HTML files
      // located in public/quizzes/. If a user visits a localized URL such as
      // /:lang/quizzes/*.html (via bookmarks, direct URL input, or legacy links),
      // the Vue router catches it here and performs a full browser redirect (window.location.replace)
      // to the root /quizzes/*.html path. This ensures that static HTML assets (such as relative CSS
      // like 'quiz_style.css' and scripts) resolve correctly without broken relative paths.
      // NOTE: Once all legacy quizzes are migrated to Vue (ModularTaskEngine), this route
      // can be cleanly removed.
      {
        path: 'quizzes/:legacyPath(.*)',
        name: 'LegacyQuizzesRedirect',
        beforeEnter(to) {
          const path = to.params.legacyPath;
          if (path && (path.endsWith(HTML_EXTENSION) || path.includes(HTML_EXTENSION))) {
            const queryStr = window.location.search || '';
            const hashStr = window.location.hash || '';
            window.location.replace(`/quizzes/${path}${queryStr}${hashStr}`);
          }
        }
      },
      {
        path: 'new-quizzes',
        name: 'NewMasterQuiz',
        component: () => import('../components/quizEngines/NewMasterQuiz/NewMasterQuizRunner.vue'),
        props: route => ({
          taskId: route.query.task || 'possessive_pronouns',
          level: route.query.level || 'a1',
          lesson: route.query.lesson || 3,
          requiredSuccessStreak: route.query.streak ? parseInt(route.query.streak, 10) : 2,
          baseStepInterval: route.query.interval ? parseInt(route.query.interval, 10) : 3
        })
      },
      {
        path: 'universal-quizzes',
        name: 'UniversalQuizEngine',
        component: () => import('../components/quizEngines/UniversalQuizEngine/UniversalQuizRunner.vue'),
        props: route => ({
          taskId: route.query.task || 'possessive_pronouns',
          level: route.query.level || 'a1',
          lesson: route.query.lesson || 3,
          requiredSuccessStreak: route.query.streak ? parseInt(route.query.streak, 10) : 2,
          baseStepInterval: route.query.interval ? parseInt(route.query.interval, 10) : 3
        })
      },
      {
        path: 'modular-quizzes',
        name: 'ModularQuizEngine',
        meta: { hideHeaderFooter: true },
        component: () => import('../components/quizEngines/ModularTaskEngine/visualizer/ModularQuizRunner.vue'),
        props: route => ({
          topic: route.query.topic || 'konektors'
        })
      },
      {
        path: 'sentence-builder',
        name: 'SentenceBuilderQuiz',
        meta: { hideHeaderFooter: true },
        component: () => import('../components/quizEngines/SentenceBuilder/SentenceBuilderRunner.vue'),
        props: route => ({
          topic: route.query.topic || route.query.task || 'konjunktiv_two_branches',
          totalStepsLimit: route.query.limit ? parseInt(route.query.limit, 10) : null
        })
      },
      {
        path: 'modular-sentence',
        name: 'ModularSentenceQuiz',
        meta: { hideHeaderFooter: true },
        component: () => import('../components/quizEngines/ModularSentenceEngine/visualizer/ModularSentenceRunner.vue'),
        props: route => ({
          topic: route.query.topic || 'konjunktiv_extended',
          totalStepsLimit: route.query.limit ? parseInt(route.query.limit, 10) : null
        })
      },
      {
        path: 'topological-sentence',
        name: 'TopologicalSentenceQuiz',
        meta: { hideHeaderFooter: true },
        component: () => import('../components/quizEngines/TopologicalSentenceEngine/visualizer/ModularSentenceRunner.vue'),
        props: route => ({
          topic: route.query.topic || 'konjunktiv_extended',
          totalStepsLimit: route.query.limit ? parseInt(route.query.limit, 10) : null
        })
      },
      {
        path: 'quiz-games',
        name: 'QuizGames',
        component: () => import('../views/QuizGames.vue')
      },
      {
        path: 'audit-inspector',
        alias: ['error-audit', 'mistakes-audit', 'diagnostic-audit'],
        name: 'ErrorAuditInspector',
        component: () => import('../views/ErrorAuditInspectorView.vue')
      },
      {
        path: 'statistics',
        alias: 'progress',
        name: 'GlobalProgressStats',
        component: () => import('../views/GlobalStatsView.vue')
      },
      {
        path: 'advanced-statistics',
        alias: ['advanced-stats', 'stats-advanced', 'comb-statistics'],
        name: 'AdvancedProgressStats',
        component: () => import('../views/AdvancedStatsView.vue')
      },
      {
        path: 'test-game',
        name: 'TestGame1',
        meta: { hideHeaderFooter: true },
        component: () => import('../TestGame1/ui/TestGameContainer.vue')
      },
      {
        path: 'test-game-2',
        name: 'TestGame2',
        meta: { hideHeaderFooter: true },
        component: () => import('../testGame2/ui/TestGame2Container.vue')
      },
      {
        path: 'grammar-citadel',
        alias: 'citadel',
        name: 'GrammarCitadel',
        component: () => import('../views/GrammarCitadelView.vue')
      },
      {
        path: 'linguistic-model',
        alias: ['grammar-model', 'linguistic-ontology'],
        name: 'LinguisticModel',
        component: () => import('../views/LinguisticModelView.vue')
      },
      {
        path: 'levels',
        name: 'LevelSelection',
        component: () => import('../views/LevelSelection.vue')
      },
      {
        path: 'level/:levelId/lessons',
        name: 'LessonSelection',
        component: () => import('../views/LessonSelection.vue'),
        props: true
      },
      {
        path: 'level/:levelId/lesson/:lessonId', // Add :section? back
        name: 'Lesson',
        component: () => import('../views/Lesson.vue'),
        props: true,
        children: [
          {
            path: '',
            name: 'LessonDefault',
            redirect: { name: 'LessonStats' }
          },
          {
            path: 'stats',
            name: 'LessonStats',
            component: () => import('../components/LessonSections/LessonStats.vue'),
            props: true
          },
          {
            path: 'video',
            name: 'LessonVideo',
            component: () => import('../components/LessonSections/LessonVideo.vue'),
            props: true
          },
          {
            path: 'dictionary',
            name: 'LessonDictionary',
            component: () => import('../components/LessonSections/LessonDictionary.vue'),
            props: true
          },
          {
            path: 'audioMp3',
            name: 'LessonAudioMp3',
            component: () => import('../components/LessonSections/LessonAudioMp3.vue'),
            props: true
          },
          {
            path: 'flashcards',
            name: 'LessonFlashcards',
            component: () => import('../components/LessonSections/LessonFlashcards.vue'),
            props: true
          },
          {
            path: 'audioCards',
            name: 'LessonAudioCards',
            component: () => import('../components/LessonSections/LessonAudioCards.vue'),
            props: true
          },
          {
            path: 'audioExercise',
            name: 'LessonAudioExercise',
            component: () => import('../components/LessonSections/LessonAudioExercise.vue'),
            props: true
          },
          {
            path: 'dialogue',
            name: 'LessonDialogue',
            component: () => import('../components/LessonSections/LessonDialogue.vue'),
            props: true
          },
          {
            path: 'dialogueFeedback',
            name: 'LessonDialogueFeedback',
            component: () => import('../components/LessonSections/LessonDialogueFeedback.vue'),
            props: true
          },
          {
            path: 'reading',
            name: 'LessonReading',
            component: () => import('../components/LessonSections/LessonReading.vue'),
            props: true
          },
          {
            path: 'audiobooks',
            name: 'LessonAudiobooks',
            component: () => import('../components/LessonSections/LessonAudiobooks.vue'),
            props: true
          },
          {
            path: 'sprechen',
            name: 'LessonSprechen',
            component: () => import('../components/LessonSections/LessonSprechen.vue'),
            props: true
          }
        ]
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to, from, next) => {
  if (!to.params.lang) {
    next({ ...to, params: { lang: 'en' }, query: to.query });
  } else {
    next();
  }
});

export default router